import React from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

import { Artefact, ARTEFACT_TYPE_META } from "@/types/artefacts";
import { useArtefactActions } from "@/hooks/useArtefactActions";

import { ArtefactActions } from "./ArtefactActions";

interface ArtefactHeaderProps {
  artefact: Artefact;
  /**
   * Shrinks the band for a short card. See `COMPACT_ARTEFACT_HEIGHT`, which is
   * where the threshold and the arithmetic behind it are recorded.
   */
  isCompact?: boolean;
}

export const ArtefactHeader: React.FC<ArtefactHeaderProps> = ({ artefact, isCompact = false }) => {
  const typeMeta = ARTEFACT_TYPE_META[artefact.type];
  const { formatTimestamp } = useArtefactActions();
  // Empty for an unparseable timestamp, so the segment and its bullet are dropped
  // together rather than leaving a separator with nothing after it.
  const timestamp = formatTimestamp(artefact.timestamp);

  return (
    // `gap-3` rather than `justify-between` alone: once the title column shrinks at
    // a narrow panel width the two groups would otherwise abut. `mb-3` matches the
    // padding the viewer puts below the rule, so the rule sits centred in its own
    // gutter now that it is the boundary of a frozen band rather than an in-flow
    // separator.
    <div className="flex items-start justify-between gap-3 mb-3">
      {/* `items-start` rather than `items-center`: a title long enough to wrap made
          the centred icon float to the middle of the text block, leaving a gap above
          it and a ragged left edge. Top alignment matches the collapsed artefact card
          this header expands from. */}
      <div className="flex items-start gap-3 min-w-0">
        <div
          className={`shrink-0 rounded-xl bg-default-100 ${isCompact ? "p-2" : "p-3"} ${typeMeta.color}`}
        >
          <Icon height={isCompact ? 16 : 20} icon={typeMeta.icon} width={isCompact ? 16 : 20} />
        </div>

        {/* `min-w-0` lets this column shrink below its content, without which a long
            title pushes the row wider than the modal instead of wrapping. */}
        <div className="min-w-0">
          {/* Clamped to two lines, as the collapsed card does. An artefact title is
              often the opening sentence of the document, so left unclamped it ran to
              three or more lines and dominated the header. On a short card it drops
              to a single line, because the band does not scroll away and a wrapped
              title there costs a fifth of everything left to read. */}
          <h2
            className={`font-semibold leading-tight text-foreground ${
              isCompact ? "text-base line-clamp-1" : "text-lg line-clamp-2"
            }`}
          >
            {artefact.title}
          </h2>
          {/*
            Each segment is `whitespace-nowrap` and the row wraps between them rather
            than inside them. Without that the row is a single non-wrapping flex line
            whose items shrink below their content, so a narrow modal broke the value
            itself and rendered `14/09/2026,` above `11:09`. `leading-tight` and the
            small `gap-y` then keep the block compact when it does wrap onto a second
            line, which is where the apparent padding above and below came from.
          */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs leading-tight text-default-500">
            <span className="whitespace-nowrap">{typeMeta.label}</span>
            {timestamp && (
              <>
                <span>•</span>
                <span className="whitespace-nowrap">{timestamp}</span>
              </>
            )}
            {artefact.metadata?.estimatedSize && (
              <>
                <span>•</span>
                <span className="whitespace-nowrap">{artefact.metadata.estimatedSize}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <ArtefactActions artefact={artefact} />
    </div>
  );
};
